import { Anchor, Flex, Stack } from '@atlaskit/primitives/compiled';
import Heading from '@atlaskit/heading';
import Image from '@atlaskit/image';
import { cssMap, cx } from '@atlaskit/css';
import { token } from '@atlaskit/tokens';
import { type FunctionComponent, useCallback, useEffect } from 'react';
import { useIntl } from 'react-intl';
import { useFirePostOfficeAnalyticsEvents } from '@post-office/analytics';
import { useMessageInstanceEvent } from '@post-office/message-instance-event-frontend-client';
import { JiraSideNavMessage } from '@post-office/components--jira-side-nav-message';

import { messages } from './messages';
import { VARIANT_CLI } from '../../../../../constants';
import RovoDevIconSVG from './icon.svg';
import { type JSX } from 'react/jsx-runtime';

const DISCOVER_ROVO_DEV_URL = '/rovodev/overview?details=code';

const styles = cssMap({
	headerContainer: {
		marginBlockEnd: token('space.100'),
	},
	link: {
		textDecoration: 'none',
	},
});

const VariantIcon: FunctionComponent = () => <Image src={RovoDevIconSVG} alt="" width={40} />;

interface PromoCardProps {
	onDismissed?: () => void;
	tenantId: string;
	accountId: string;
}

/**
 * "..." opens a menu; Dismiss is the "Not interested" item inside that menu (not on the ... itself).
 * Same pattern as: JiraSideNavFirstPartyRecommendation (growth/components/jira-side-nav-first-party-recommendation)
 * which uses JiraSideNavMessage → AdControls for the "..." dropdown.
 */
const PromoCard: FunctionComponent<PromoCardProps> = ({ onDismissed, tenantId, accountId }) => {
	const { formatMessage } = useIntl();
	const { fireAnalyticsEvent } = useFirePostOfficeAnalyticsEvents();
	const { messageInstanceClicked } = useMessageInstanceEvent();

	const handleDismissedFromMenu = useCallback(() => {
		// Template-specific GASv3 analytics for dismiss (register this event for product 'jira'
		// at Dataportal Event Registry if you need it to be accepted by the pipeline).
		fireAnalyticsEvent(
			{
				eventType: 'ui',
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'post-office.jira-side-nav-rovo-dev-adoption-ipm.dismiss',
			},
			{
				tenantId,
				accountId,
				variant: VARIANT_CLI,
			},
		);
		onDismissed?.();
	}, [fireAnalyticsEvent, tenantId, accountId, onDismissed]);

	const handleDiscoverClick = useCallback(() => {
		void messageInstanceClicked({
			messageEventType: 'clicked',
			clickedElement: {
				clickIdentifier: 'discoverRovodev',
				clickedElement: 'link',
			},
			action: {
				actionType: 'open',
				destinationUrl: DISCOVER_ROVO_DEV_URL,
			},
			additionalMetadataFromFrontend: {
				variant: VARIANT_CLI,
			},
		});

		fireAnalyticsEvent(
			{
				eventType: 'ui',
				action: 'clicked',
				actionSubject: 'link',
				actionSubjectId: 'post-office.jira-side-nav-rovo-dev-adoption-ipm.discover-rovodev',
			},
			{
				tenantId,
				accountId,
				variant: VARIANT_CLI,
			},
		);
	}, [messageInstanceClicked, fireAnalyticsEvent, tenantId, accountId]);

	useEffect(() => {
		fireAnalyticsEvent(
			{
				eventType: 'ui',
				action: 'viewed',
				actionSubject: 'ipm',
				actionSubjectId: 'jira-side-nav-rovo-dev-adoption-ipm',
			},
			{
				tenantId,
				accountId,
				variant: VARIANT_CLI,
			},
		);
	}, [fireAnalyticsEvent, tenantId, accountId]);

	const bodyContent = (
		<Flex xcss={cx(styles.headerContainer)} justifyContent="space-between" alignItems="start">
			<Stack space="space.100">
				<VariantIcon />
				<Heading size="xsmall">{formatMessage(messages.heading)}</Heading>
				<Anchor
					href={DISCOVER_ROVO_DEV_URL}
					target="_blank"
					xcss={styles.link}
					onClick={handleDiscoverClick}
				>
					{formatMessage(messages.discoverButton)}
				</Anchor>
			</Stack>
		</Flex>
	);

	return (
		<JiraSideNavMessage
			onDismissed={handleDismissedFromMenu}
			bodyContent={bodyContent}
			showWhyAmISeeingThis={false}
		/>
	);
};

type VariantRenderProps = {
	onDismissed?: () => void;
	tenantId: string;
	accountId: string;
};

export const render = ({ onDismissed, tenantId, accountId }: VariantRenderProps): JSX.Element => (
	<PromoCard onDismissed={onDismissed} tenantId={tenantId} accountId={accountId} />
);

export default render;
